import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import BriocheLogo from './BriocheLogo';
import LogoUploader from './LogoUploader';
import useDashboardStore from '../store/dashboardStore';

const { FiX, FiLock, FiUpload, FiTrash2, FiImage, FiShield, FiDatabase, FiSettings, FiAlertTriangle, FiCheck } = FiIcons;

const AdminPanel = ({ isOpen, onClose }) => {
  const [pin, setPin] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const {
    toggleUploadModal,
    toggleLogoUploadModal,
    toggleClearModal,
    logoUploadModalOpen,
    toggleLogoUploadModal: closeLogoModal
  } = useDashboardStore();

  // Reset state when panel closes
  useEffect(() => {
    if (!isOpen) {
      setPin('');
      setIsAuthenticated(false);
      setError('');
      setLoading(false);
    }
  }, [isOpen]);

  const handlePinSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Simulate authentication delay
    setTimeout(() => {
      // Check for valid PINs: 73562 or 9139 (failsafe)
      if (pin === '73562' || pin === '9139') {
        setIsAuthenticated(true);
        setError('');
      } else {
        setError('Invalid PIN. Access denied.');
        setPin('');
      }
      setLoading(false);
    }, 800);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPin('');
    setError('');
  };

  const handleUploadClick = () => {
    toggleUploadModal();
    onClose(); // Close admin panel after action
  };

  const handleLogoUploadClick = () => {
    toggleLogoUploadModal();
    // Don't close admin panel for logo upload as it has its own modal
  };

  const handleClearClick = () => {
    toggleClearModal();
    onClose(); // Close admin panel after action
  };

  const handleLogoUploadSuccess = () => {
    console.log('Logo upload successful from admin panel');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-brand-card rounded-card p-8 w-full max-w-md mx-4 shadow-card border border-brand-accent/20"
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-brand-accent/20 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiShield} className="w-5 h-5 text-brand-accent" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-brand-fg">Admin Panel</h3>
                <p className="text-sm text-brand-muted">
                  {isAuthenticated ? 'Data Management Controls' : 'Authentication Required'}
                </p>
              </div>
            </div>
            <motion.button
              onClick={onClose}
              className="p-2 hover:bg-brand-muted2/20 rounded-button transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiX} className="w-5 h-5 text-brand-muted" />
            </motion.button>
          </div>

          {!isAuthenticated ? (
            // Authentication Form
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="text-center mb-6">
                <BriocheLogo className="w-16 h-16 mx-auto mb-4" />
                <h4 className="text-brand-fg font-medium mb-2">Secure Access</h4>
                <p className="text-brand-muted text-sm">
                  Username: admin@attitude-dynamics.com
                </p>
              </div>

              <form onSubmit={handlePinSubmit} className="space-y-4">
                {error && (
                  <motion.div
                    className="bg-brand-bad/10 border border-brand-bad/20 rounded-lg p-3"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-brand-bad" />
                      <span className="text-brand-bad text-sm">{error}</span>
                    </div>
                  </motion.div>
                )}

                <div>
                  <label className="block text-brand-fg text-sm font-medium mb-2">
                    Security PIN
                  </label>
                  <div className="relative">
                    <SafeIcon
                      icon={FiLock}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brand-muted"
                    />
                    <input
                      type="password"
                      value={pin}
                      onChange={(e) => setPin(e.target.value)}
                      required
                      maxLength={5}
                      className="w-full bg-brand-muted2/10 text-brand-fg rounded-button pl-10 pr-4 py-3 border border-brand-muted2/20 focus:border-brand-accent focus:outline-none transition-colors text-center text-lg tracking-widest"
                      placeholder="•••••"
                      autoFocus
                    />
                  </div>
                  <p className="text-brand-muted2 text-xs mt-2 text-center">
                    Enter 5-digit security PIN
                  </p>
                </div>

                <motion.button
                  type="submit"
                  disabled={loading || pin.length !== 5}
                  className="w-full bg-brand-accent hover:bg-brand-accent/80 text-brand-bg font-medium py-3 rounded-button transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={!loading && pin.length === 5 ? { scale: 1.02 } : {}}
                  whileTap={!loading && pin.length === 5 ? { scale: 0.98 } : {}}
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-brand-bg border-t-transparent rounded-full animate-spin" />
                      <span>Authenticating...</span>
                    </div>
                  ) : (
                    'Access Admin Panel'
                  )}
                </motion.button>
              </form>

              <div className="mt-6 p-3 bg-brand-muted2/10 rounded-lg border border-brand-muted2/20">
                <div className="flex items-center space-x-2 mb-2">
                  <SafeIcon icon={FiSettings} className="w-4 h-4 text-brand-muted" />
                  <span className="text-brand-muted text-sm font-medium">Security Notice</span>
                </div>
                <p className="text-brand-muted2 text-xs">
                  This admin panel provides access to sensitive data management functions.
                  Only authorized personnel should access these controls.
                </p>
              </div>
            </motion.div>
          ) : (
            // Admin Controls (Authenticated)
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              {/* Success Message */}
              <motion.div
                className="mb-6 p-3 bg-brand-good/10 border border-brand-good/20 rounded-lg"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-brand-good" />
                  <span className="text-brand-good text-sm font-medium">Access Granted</span>
                </div>
                <p className="text-brand-good text-xs mt-1">
                  Welcome to the admin panel. Handle with care.
                </p>
              </motion.div>

              {/* Data Management Controls */}
              <div className="space-y-3">
                <h4 className="text-brand-fg font-medium mb-4 flex items-center space-x-2">
                  <SafeIcon icon={FiDatabase} className="w-4 h-4" />
                  <span>Data Management</span>
                </h4>

                {/* Upload Sales Data */}
                <motion.button
                  onClick={handleUploadClick}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-brand-fg hover:bg-brand-muted2/10 rounded-button transition-colors text-left border border-brand-muted2/20"
                  whileHover={{ x: 4, scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <div className="w-8 h-8 bg-brand-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiUpload} className="w-4 h-4 text-brand-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Upload Sales Data</p>
                    <p className="text-xs text-brand-muted">Import Excel/CSV files with sales records</p>
                  </div>
                </motion.button>

                {/* Upload Logo */}
                <motion.button
                  onClick={handleLogoUploadClick}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-brand-fg hover:bg-brand-muted2/10 rounded-button transition-colors text-left border border-brand-muted2/20"
                  whileHover={{ x: 4, scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <div className="w-8 h-8 bg-brand-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiImage} className="w-4 h-4 text-brand-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Upload Logo</p>
                    <p className="text-xs text-brand-muted">Change dashboard branding logo</p>
                  </div>
                </motion.button>

                {/* Clear Database */}
                <motion.button
                  onClick={handleClearClick}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-brand-fg hover:bg-brand-bad/10 rounded-button transition-colors text-left border border-brand-bad/20"
                  whileHover={{ x: 4, scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <div className="w-8 h-8 bg-brand-bad/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiTrash2} className="w-4 h-4 text-brand-bad" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-brand-bad">Clear Database</p>
                    <p className="text-xs text-brand-muted">Permanently delete all sales data</p>
                  </div>
                </motion.button>
              </div>

              {/* Logout Button */}
              <div className="mt-6 pt-4 border-t border-brand-muted2/20">
                <motion.button
                  onClick={handleLogout}
                  className="w-full bg-brand-muted2/20 hover:bg-brand-muted2/30 text-brand-fg py-2 px-4 rounded-button transition-colors text-sm"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Logout from Admin Panel
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Logo Upload Modal */}
          <LogoUploader
            isOpen={logoUploadModalOpen}
            onClose={closeLogoModal}
            onSuccess={handleLogoUploadSuccess}
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AdminPanel;